import React from 'react';
import * as styles from './PostDraftBarPublishButton.module.scss';
import PostStateBarOverflowMenu from './PostStateBarOverflowMenu';
import usePublishPost from './usePublishPost';
import useAppSelector from '../app/redux/useAppSelector';
import type { ButtonProps } from '../components/Button';
import Button from '../components/Button';
import { MenuItem } from '../components/Menu';
import Tooltip from '../components/Tooltip';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import {
  PostLinkAccess,
  PostType,
  UserType,
} from '../graphql/generated/graphql';
import publishIcon from '../icons/non-prefilled/16x16/publish.svg';
import { createPostPath } from '../utils/url/postPath';

const POST_DRAFT_BAR_PUBLISH_BUTTON_POST_FRAGMENT = gql(`
  fragment PostDraftBarPublishButton_post on Post {
    id
    linkAccess
    type
    topics {
      id
    }
  }
`);

interface PostPublishButtonProps extends ButtonProps {
  post: DocumentType<typeof POST_DRAFT_BAR_PUBLISH_BUTTON_POST_FRAGMENT>;
}

const PostDraftBarPublishButton = ({
  post,
  ...buttonProps
}: PostPublishButtonProps) => {
  const postTopicIds = post.topics.map(({ id }) => id);
  const isGuest = useAppSelector(
    (state) => state.session.user.type === UserType.Guest,
  );

  const publishPost = usePublishPost(post.id);

  const hasTopic = postTopicIds.length > 0;
  const isDisabled = isGuest && !hasTopic;
  const tooltip = isDisabled
    ? 'Guests must publish\nposts in at least\none topic'
    : undefined;

  return (
    <div className={styles.container}>
      {post.linkAccess === PostLinkAccess.Internal ? (
        <>
          {post.type === PostType.Template && (
            <PostStateBarOverflowMenu>
              <MenuItem
                disabled={isDisabled}
                tooltip={tooltip}
                to={createPostPath({ templateId: post.id })}
                icon={publishIcon}
              >
                Create post
              </MenuItem>
            </PostStateBarOverflowMenu>
          )}
          <Tooltip placement="bottom" content={tooltip}>
            <Button
              disabled={isDisabled}
              variant="primary"
              onClick={() => publishPost(true)}
              {...buttonProps}
            >
              Publish
            </Button>
          </Tooltip>
        </>
      ) : (
        <>
          <PostStateBarOverflowMenu>
            <MenuItem
              disabled={isDisabled}
              tooltip={tooltip}
              onClick={() => publishPost(true)}
              icon={publishIcon}
            >
              Publish
            </MenuItem>
          </PostStateBarOverflowMenu>
          {post.type === PostType.Template && (
            <Tooltip placement="bottom" content={tooltip}>
              <Button
                disabled={isDisabled}
                variant="primary"
                to={createPostPath({ templateId: post.id })}
                {...buttonProps}
              >
                Create post
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
};

export default PostDraftBarPublishButton;
